'use client'

import React from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { cn } from '@kiao-voice/ui-kit'
import { HowList } from '../../components/site/how-list'
import {
  PAIR_FIELD_CLASS,
  PAIR_FRAME_CLASS,
  PAIR_GRID_CLASS,
  PAIR_SPEECH_CLASS,
  PairArrow,
  PairLabel,
} from '../../components/site/pair-frame'
import { Reveal } from '../../components/site/reveal'
import { SectionHead } from '../../components/site/section-head'
import { SiteSection } from '../../components/site/section'
import { DICTATION_ORGANIZE_NOTES } from './dictation-example'

/**
 * 一個例子＝一個外框：你隨口說 → 貼出來的字 → 做了哪三件事，全部框在同一塊裡，
 * 手機上往下滑也看得出是同一件事。結果裡的圈號 1 2 3 對到框內下方的三條說明。
 */

/** 結果裡的圈號，對到下方的第 N 條說明。 */
function ExampleRef({ children }: { children?: React.ReactNode }) {
  return (
    <span
      aria-hidden="true"
      className="ml-[3px] inline-grid size-4 place-items-center rounded-full border border-foreground font-mono text-3xs leading-none [vertical-align:0.55em]"
    >
      {children}
    </span>
  )
}

const RESULT_ITEM_KEYS = ['outItem1', 'outItem2']

export function DictationBeforeAfterSection() {
  const { t } = useTranslation(['web'])

  return (
    <SiteSection trackingKey="before_after">
      <Reveal>
        <SectionHead
          eyebrow={t('web:dictationPage.beforeAfter.eyebrow')}
          titleKey="web:dictationPage.beforeAfter.title"
          lead={t('web:dictationPage.beforeAfter.lead')}
        />
      </Reveal>
      <Reveal className={cn('mt-(--gap-xl)', PAIR_FRAME_CLASS)}>
        <div className={PAIR_GRID_CLASS}>
          <div>
            <PairLabel>{t('web:dictationPage.example.saidLabel')}</PairLabel>
            <p className={PAIR_SPEECH_CLASS}>
              <Trans
                t={t}
                i18nKey="web:dictationPage.example.said"
                components={{
                  // 刪節線用 --border 在這個字級下幾乎看不見，而「講錯的被改掉」
                  // 正是左欄唯一要傳達的事，線要看得到。
                  s: (
                    <s className="[text-decoration-color:var(--muted)] [text-decoration-thickness:1.5px]" />
                  ),
                }}
              />
            </p>
          </div>
          <PairArrow />
          <div>
            <PairLabel done>{t('web:dictationPage.example.outLabel')}</PairLabel>
            <div className={cn(PAIR_FIELD_CLASS, PAIR_SPEECH_CLASS)}>
              <p>
                <Trans
                  t={t}
                  i18nKey="web:dictationPage.example.outIntro"
                  components={{ ref: <ExampleRef /> }}
                />
              </p>
              <ul className="mt-2.5 list-disc pl-[1.3em] marker:text-muted">
                {RESULT_ITEM_KEYS.map((key, index) => (
                  <li key={key} className={cn(index > 0 && 'mt-2')}>
                    <Trans
                      t={t}
                      i18nKey={`web:dictationPage.example.${key}`}
                      components={{
                        ref: <ExampleRef />,
                        mark: (
                          <mark className="border-b-[1.5px] border-brand bg-transparent text-foreground" />
                        ),
                      }}
                    />
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
        <HowList
          items={DICTATION_ORGANIZE_NOTES.map((note) => ({
            title: t(`web:${note.titleKey}`),
            description: t(`web:${note.descriptionKey}`),
          }))}
          note={t('web:dictationPage.how.note')}
        />
      </Reveal>
    </SiteSection>
  )
}
